import type { EngineBridge, EngineListener } from './bridge';
import type { EngineEvent } from './protocol';

const ENGINE_JS = '/engine/stockfish-18-lite-single.js';
const DEFAULT_DEPTH = 14;

/**
 * Stockfish lite-single via classic Worker (UCI strings).
 * No COOP/COEP required. Score + first PV move for the eval badge.
 */
export function createStockfishBridge(): EngineBridge {
  const listeners = new Set<EngineListener>();
  let worker: Worker | null = null;
  let ready = false;
  let initPromise: Promise<void> | null = null;
  let currentRequestId: string | null = null;

  const emit = (event: EngineEvent) => {
    for (const l of listeners) l(event);
  };

  function onWorkerMessage(ev: MessageEvent) {
    const line = typeof ev.data === 'string' ? ev.data : String(ev.data ?? '');
    if (!line) return;

    // Multi-line batches
    if (line.includes('\n')) {
      for (const part of line.split('\n')) {
        if (part.trim()) onWorkerMessage({ data: part } as MessageEvent);
      }
      return;
    }

    if (line === 'uciok' || line === 'readyok') {
      ready = true;
      emit({ type: 'ready' });
      return;
    }

    if (line.startsWith('info ') && currentRequestId) {
      const depthM = line.match(/\bdepth (\d+)/);
      const mateM = line.match(/\bscore mate (-?\d+)/);
      const cpM = line.match(/\bscore cp (-?\d+)/);
      const pvM = line.match(/\bpv\s+([a-h][1-8][a-h][1-8][qrbn]?)/i);
      if (!depthM) return;
      const depth = Number(depthM[1]);
      const pvUci = pvM?.[1]?.toLowerCase();
      if (mateM) {
        emit({
          type: 'info',
          requestId: currentRequestId,
          depth,
          mate: Number(mateM[1]),
          pvUci,
        });
      } else if (cpM) {
        emit({
          type: 'info',
          requestId: currentRequestId,
          depth,
          cp: Number(cpM[1]),
          pvUci,
        });
      }
      return;
    }

    if (line.startsWith('bestmove ') && currentRequestId) {
      const token = line.split(/\s+/)[1] ?? '';
      const uci = /^[a-h][1-8][a-h][1-8][qrbn]?$/i.test(token)
        ? token.toLowerCase()
        : '';
      emit({ type: 'bestmove', requestId: currentRequestId, uci });
      return;
    }
  }

  return {
    init() {
      if (initPromise) return initPromise;
      initPromise = new Promise<void>((resolve, reject) => {
        try {
          worker = new Worker(ENGINE_JS);
          worker.onmessage = onWorkerMessage;
          worker.onerror = (err) => {
            emit({ type: 'error', message: err.message || 'Worker error' });
            reject(err);
          };
          // UCI handshake
          worker.postMessage('uci');
          worker.postMessage('isready');
          // readyok arrives async — resolve after short wait or first ready
          const t = window.setTimeout(() => {
            if (!ready) {
              // still resolve; messages may arrive later
              resolve();
            }
          }, 8000);
          const unsub = (() => {
            const l: EngineListener = (e) => {
              if (e.type === 'ready') {
                window.clearTimeout(t);
                listeners.delete(l);
                resolve();
              }
            };
            listeners.add(l);
            return () => listeners.delete(l);
          })();
          void unsub;
        } catch (e) {
          emit({
            type: 'error',
            message: e instanceof Error ? e.message : 'Failed to start engine',
          });
          reject(e);
        }
      });
      return initPromise;
    },

    analyze({ requestId, fen, depth = DEFAULT_DEPTH }) {
      if (!worker) {
        emit({ type: 'error', requestId, message: 'Engine not initialized' });
        return;
      }
      currentRequestId = requestId;
      worker.postMessage('stop');
      worker.postMessage(`position fen ${fen}`);
      worker.postMessage(`go depth ${depth}`);
    },

    stop() {
      worker?.postMessage('stop');
    },

    dispose() {
      try {
        worker?.postMessage('quit');
      } catch {
        /* ignore */
      }
      worker?.terminate();
      worker = null;
      ready = false;
      initPromise = null;
      currentRequestId = null;
      listeners.clear();
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
